// © Microsoft Corporation. All rights reserved.

import { check_circled, plus, publisherTabPremium } from "../utils/icons";

import { SocialProps } from "../Social.props";
import React from "react";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../utils/enums";
import { getExternalURL } from "../utils/urlHelper";
import { initializeSubscriptionSdk, getSubscriptionStatus } from "@msnews/subscription-sdk-wrapper";
import { getXPayToken } from "@msnews/social-data-service";

export enum SubscribeButtonStateEnum {
    Following = "Following",
    Follow = "Follow",
    Subscribed = "Subscribed"
}

export interface PublisherSubscribeFollowButtonState {
    isSubscribed: Boolean
}

export default class PublisherSubscribeFollowButton extends React.Component<SocialProps, PublisherSubscribeFollowButtonState> {
    buttonConfig = {
        [SubscribeButtonStateEnum.Follow]: {
            text: this.props.t.FollowButtonFollowText,
            icon: plus,
            container: this.props.managedClasses.publisherSubscribeFollowButtonInactiveContainer
        },
        [SubscribeButtonStateEnum.Following]: {
            text: this.props.t.FollowButtonFollowingText,
            icon: check_circled,
            container: this.props.managedClasses.publisherSubscribeFollowButtonActiveContainer
        },
        [SubscribeButtonStateEnum.Subscribed]: {
            text: this.props.t.PublisherProfilePageSubscribed,
            icon: publisherTabPremium,
            container: this.props.managedClasses.publisherSubscribeFollowButtonActiveContainer
        }
    };

    private subscriptionAndFollowChecked: boolean;

    constructor(props) {
        super(props);
        this.state = {
            isSubscribed: false
        };
    }

    componentDidMount() {
        initializeSubscriptionSdk();
        const { me, publisherSubscribeFollowButtonData } = this.props;
        if (!me) {
            this.props.usersConnector.fetchMe();
        } else if (publisherSubscribeFollowButtonData) {
            this.checkSubscriptionAndFollowStatus();
        }
    }

    componentDidUpdate(prevProps) {
        const { me, publisherSubscribeFollowButtonData } = this.props;
        if (!this.subscriptionAndFollowChecked && me && publisherSubscribeFollowButtonData) {
            if (publisherSubscribeFollowButtonData) {
                this.checkSubscriptionAndFollowStatus();
            }
        }
    }

    async checkSubscriptionAndFollowStatus() {
        this.subscriptionAndFollowChecked = true;
        const { me, publisherSubscribeFollowButtonData } = this.props;
        const { fetchUser, fetchIsUserFollowed } = this.props.usersConnector;
        const publisherId = publisherSubscribeFollowButtonData.publisherId;

        if (!publisherId) { return; }

        fetchUser(publisherId)
            .then(() => {
                fetchIsUserFollowed(publisherId);
            });

        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (!isAnonymous) {
            getSubscriptionStatus({
                authToken: await getXPayToken(),
                publisherId
            }).then(response => {
                if (response.status) {
                    this.setState(() => ({ isSubscribed: response.data }));
                }
            });
        }
    }

    render() {
        const { managedClasses, t, publisherSubscribeFollowButtonData } = this.props;
        const currentState = this.getCurrentState();

        if (!publisherSubscribeFollowButtonData || !currentState) { return null; }

        const config = this.getButtonConfig(currentState);

        return (
            <React.Fragment>
                <button
                    aria-live="assertive"
                    type="button"
                    name={t.FollowButtonTitle}
                    className={classNames(
                        managedClasses.publisherSubscribeFollowButtonContainer,
                        config.container
                    )}
                    onClick={(e) => this.onButtonClick(e)}
                >
                    <i className="cwt-icon"> { config.icon } </i>
                    { config.text }
                </button>
            </React.Fragment>
        );
    }

    onButtonClick(e) {
        const currentState = this.getCurrentState();
        if (currentState == SubscribeButtonStateEnum.Following || currentState == SubscribeButtonStateEnum.Subscribed) {
            this.redirectToPublisherProfile();
        } else {
            this.followPublisher();
        }
    }

    redirectToPublisherProfile() {
        const { publisherSubscribeFollowButtonData: { publisherId }, market } = this.props;
        window.open(getExternalURL(`${enums.externalUrls.channelPage}${publisherId}`, market));
    }

    followPublisher() {
        const { me, publisherSubscribeFollowButtonData: { publisherId } } = this.props;
        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (isAnonymous) {
            this.signIn();
        } else {
            this.props.usersConnector.followUser(publisherId, true);
        }
    }

    getCurrentState() {
        const { me, publisherSubscribeFollowButtonData, users } = this.props;

        const publisherId = publisherSubscribeFollowButtonData && publisherSubscribeFollowButtonData.publisherId;
        if (!publisherSubscribeFollowButtonData || !publisherId || !users || !users[publisherId] || !users[publisherId].isFollowingFetched) {
            return null;
        }

        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (isAnonymous) {
            return SubscribeButtonStateEnum.Follow;
        }

        const { isFollowing } = users[publisherId].data;
        if (this.state.isSubscribed) {
            return SubscribeButtonStateEnum.Subscribed;
        } else {
            return isFollowing ? SubscribeButtonStateEnum.Following : SubscribeButtonStateEnum.Follow;
        }
    }

    getButtonConfig(state: SubscribeButtonStateEnum) {
        return this.buttonConfig[state];
    }

    signIn() {
        this.props.popupConnector.openPopup("multiLoginPage");
    }
}
